"use strict";
/* globals module */
/**
 * @module mediaGalleryInstanceFilesClient
 * @description  Media Gallery Instance Files Client provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic Files Route Service to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
*/
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var __1 = require("../");
var MediaGalleryInstanceFilesClient = /** @class */ (function () {
    function MediaGalleryInstanceFilesClient(mediaGalleryInstanceFilesRoute, MediaGalleryInstanceFilesBatchClient, apiClient) {
        this.mediaGalleryInstanceFilesRoute = mediaGalleryInstanceFilesRoute;
        this.MediaGalleryInstanceFilesBatchClient = MediaGalleryInstanceFilesBatchClient;
        this.apiClient = apiClient;
    }
    Object.defineProperty(MediaGalleryInstanceFilesClient.prototype, "routeDefinition", {
        get: function () {
            return this.mediaGalleryInstanceFilesRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MediaGalleryInstanceFilesClient.prototype, "batch", {
        get: function () {
            return this.MediaGalleryInstanceFilesBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of file resources matching the given criteria.
     * @method
     * @param mediaGalleryId Media Galleryslug or id which uniquely identifies media gallery whose media gallery files need to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example mediaGalleryInstanceFilesClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    ids: '1,2,3',
                    from: '',
                    to: '',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    MediaGalleryInstanceFilesClient.prototype.find = function (mediaGalleryId, options) {
        return this.apiClient.get(this.mediaGalleryInstanceFilesRoute.find(mediaGalleryId, options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns requested file resource.
     * @method
     * @param mediaGalleryId Media Galleryslug or id which uniquely identifies media gallery whose media gallery files need to be retrieved.
     * @param id Media Galleryfile id which uniquely identifies media gallery file that needs to be retrieved.
     * @param options options object that contains embed data.
     * @returns A promise that is resolved once the get action has been performed.
     * @example mediaGalleryInstanceFilesClient.get('<file-id>')
                   .then(function (data) {
                       // perform success action here
                   },
                    function (response, status, headers, config) {
                       // perform error handling here
                   });
    **/
    MediaGalleryInstanceFilesClient.prototype.get = function (mediaGalleryId, id, options) {
        return this.apiClient.get(this.mediaGalleryInstanceFilesRoute.get(mediaGalleryId, id, options));
    };
    /**
     * Returns a promise that is resolved once the unlink action has been performed. This action will remove one or many file resources from the system if successfully completed. Specified file and all its accompanying derived resources will be removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply baasicMediaGalleryFilesRouteService route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(fileEntry);
     * let uri = params['model'].links('unlink').href;
     * ```
     * @method
     * @param mediaGalleryId Media Gallery slug or id which uniquely identifies media gallery whose media gallery files need to be deleted.
     * @param data
     * @param options
     * @returns A promise that is resolved once the unlink action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action. The following action will unlink the original file resource and all accompanying derived file resources.
                    mediaGalleryInstanceFilesRoute.unlink(galleryId, fileEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    MediaGalleryInstanceFilesClient.prototype.unlink = function (mediaGalleryId, data) {
        return this.apiClient.delete(this.mediaGalleryInstanceFilesRoute.unlink(mediaGalleryId, data));
    };
    /**
     * Returns a promise that is resolved once the unlink by media gallery action has been performed. This action will remove all file resources from the system related to the requested media gallery if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply baasicMediaGalleryService route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(fileEntry);
     * let uri = params['model'].links('unlink-by-media-gallery').href;
     * ```
     * @method
     * @param mediaGalleryId Media Galleryslug or id which uniquely identifies media gallery whose media gallery files need to be deleted.
     * @param data
     * @param options
     * @returns A promise that is resolved once the unlink by media gallery action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action.
                    mediaGalleryInstanceFilesClient.unlinkByMediaGallery(fileEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    MediaGalleryInstanceFilesClient.prototype.unlinkByMediaGallery = function (mediaGalleryId, options) {
        return this.apiClient.delete(this.mediaGalleryInstanceFilesRoute.unlinkByMediaGallery(mediaGalleryId, options));
    };
    /**
     * Returns a promise that is resolved once the update file action has been performed; this action will update a file resource if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `baasicMediaGalleryFilesRouteService` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     ```
     let params = modelMapper.updateParams(fileEntry);
     let uri = params['model'].links('put').href;
     ```
     * @method
     * @param mediaGalleryId An media gallery object used to update specified media gallery resource.
     * @param data Media GalleryFile object used to update specific media gallery file data in the system.
     * @returns A promise that is resolved once the update file action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action.
                   fileEntry.description = '<description>';
                   mediaGalleryInstanceFilesClient.update(fileEntry)
                       .then(function (data) {
                           // perform success action here
                       },
                        function (response, status, headers, config) {
                           // perform error handling here
                       });
    **/
    MediaGalleryInstanceFilesClient.prototype.update = function (mediaGalleryId, data) {
        return this.apiClient.put(this.routeDefinition.update(mediaGalleryId, data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the link action has been performed; this action links file resource from other modules into the Media GalleryFiles module (For example: file resources from the Media Vault module can be linked directly into the Media GalleryFiles module).
     * @method
     * @param mediaGalleryId Media Gallery id which uniquely identifies media gallery whose media gallery files need to be linked.
     * @param data A media gallery file object that need to be inserted into the system.
     * @returns A promise that is resolved once the link action has been performed.
     * @example mediaGalleryInstanceFilesClient.link(fileObject)
                    .then(function (response, status, headers, config) {
                        // perform success handling here
                    },
                        function (response, status, headers, config) {
                        // perform error handling here
                    });
     **/
    MediaGalleryInstanceFilesClient.prototype.link = function (mediaGalleryId, data) {
        return this.apiClient.post(this.routeDefinition.link(mediaGalleryId, data), this.routeDefinition.createParams(data));
    };
    var _a, _b, _c;
    MediaGalleryInstanceFilesClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.MediaGalleryInstanceFilesRoute)),
        tslib_1.__param(1, inversify_1.inject(__1.TYPES.MediaGalleryInstanceFilesBatchClient)),
        tslib_1.__param(2, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.MediaGalleryInstanceFilesRoute !== "undefined" && __1.MediaGalleryInstanceFilesRoute) === "function" ? _a : Object, typeof (_b = typeof __1.MediaGalleryInstanceFilesBatchClient !== "undefined" && __1.MediaGalleryInstanceFilesBatchClient) === "function" ? _b : Object, typeof (_c = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _c : Object])
    ], MediaGalleryInstanceFilesClient);
    return MediaGalleryInstanceFilesClient;
}());
exports.MediaGalleryInstanceFilesClient = MediaGalleryInstanceFilesClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
*/ 
